import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Alert,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useMessageContext } from '@/hooks/useMessages';
import MessageCard from './MessageCard';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ErrorAlert from '@/components/common/ErrorAlert';

interface MessageContextDialogProps {
  open: boolean;
  messageId: number | null;
  chatId: number | null;
  contextSize?: number;
  onClose: () => void;
}

const MessageContextDialog: React.FC<MessageContextDialogProps> = ({
  open,
  messageId,
  chatId,
  contextSize = 5,
  onClose,
}) => {
  const { t } = useTranslation('components');
  const { data, isLoading, isError, error } = useMessageContext(chatId, messageId, contextSize);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle component="div">
        <Typography variant="h6" component="div">
          {t('messageContextDialog.title')}
        </Typography>
        {messageId && (
          <Typography variant="caption" color="text.secondary" component="div">
            {t('messageContextDialog.subtitle', { contextSize, messageId })}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {isLoading && <LoadingSpinner message={t('messageContextDialog.loading')} />}

        {isError && <ErrorAlert error={error} title={t('messageContextDialog.errorTitle')} />}

        {data && (
          <Box>
            {/* Messages Before */}
            {data.messages_before.length > 0 && (
              <>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('messageContextDialog.messagesBefore', { count: data.messages_before.length })}
                </Typography>
                {data.messages_before.map((msg) => (
                  <MessageCard key={msg.id} message={msg} showActions={false} />
                ))}
                <Divider sx={{ my: 2 }} />
              </>
            )}

            {/* Target Message */}
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('messageContextDialog.targetMessage')}
            </Alert>
            <MessageCard message={data.target_message} showActions={false} />

            {/* Messages After */}
            {data.messages_after.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('messageContextDialog.messagesAfter', { count: data.messages_after.length })}
                </Typography>
                {data.messages_after.map((msg) => (
                  <MessageCard key={msg.id} message={msg} showActions={false} />
                ))}
              </>
            )}

            {/* No context available */}
            {data.messages_before.length === 0 && data.messages_after.length === 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {t('messageContextDialog.noSurroundingMessages')}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          {t('messageContextDialog.closeButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MessageContextDialog;
